import React from 'react'
import { graphql } from 'gatsby'
import styled from '@emotion/styled'

import Slider, { Settings as SliderSettings } from 'react-slick'

import type { FieldNode } from '../../../drupal'
import type { WebformObject } from 'gatsby-drupal-webform'
import type { Paragraph, ParagraphComponent } from '../types'

import { classNames } from '../../ui/utils'
import { colors, media, widths } from '../../../styles/variables'
import Container from '../../Container'
import Form from '../../ui/Form'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

interface BackgroundImage {
	mid: number
	relationships: {
		field_media_image: {
			localFile: {
				childImageSharp: {
					resize: {
						src: string
					}
				}
			}
		}
	}
}

export interface HeroFormParagraph extends Paragraph {
	field_text: {
		processed: string
	}
	source: string
	body: {
		processed: string
	}
	relationships: {
		destination: FieldNode & { title: string }
		field_media_multiple: BackgroundImage[]
		webform: WebformObject
	}
}

const SlickSettings: SliderSettings = {
	autoplay: true,
	autoplaySpeed: 6000,
	dots: false,
	infinite: true,
	touchMove: false,
	swipeToSlide: false,
	speed: 1500,
	fade: true
}

const HeroForm: ParagraphComponent<HeroFormParagraph> = ({ className, paragraph }) => {
	return (
		<div className={classNames('hero', className)}>
			<Slider {...SlickSettings}>
				{paragraph.relationships.field_media_multiple.map(media => (
					<div
						key={media.mid}
						className="item"
						css={{
							backgroundImage: `url(${media.relationships.field_media_image.localFile.childImageSharp.resize.src})`,
							backgroundSize: 'cover'
						}}
					/>
				))}
			</Slider>
			<Container>
				{paragraph.body?.processed && (
					<div className="title">
						<div dangerouslySetInnerHTML={{ __html: paragraph.body.processed }} />
					</div>
				)}
				<div className="sidebar">
					<div dangerouslySetInnerHTML={{ __html: paragraph.field_text.processed }} />
					<Form
						saveSession
						webform={paragraph.relationships.webform}
						onSuccess={{
							type: 'navigate',
							destination: paragraph.relationships.destination.path.alias,
							event: `oe-${paragraph.relationships.webform.drupal_internal__id}`,
							eventData: {
								[`oe-${paragraph.relationships.webform.drupal_internal__id}-label`]:
									paragraph.relationships.destination.title
							}
						}}
						extraData={{
							source: paragraph.source
						}}
						sourceEntity={{
							entityType: 'paragraph',
							entityId: paragraph.drupal_internal__id
						}}
					/>
				</div>
			</Container>
		</div>
	)
}

export default styled(HeroForm)`
	position: relative;
	overflow: hidden;

	.slick-slider {
		position: absolute;

		width: 100%;
		height: 100%;

		${media.down('xs')} {
			display: none;
		}
	}

	.slick-arrow {
		display: none !important;
	}

	.slick-slider,
	.slick-slide > *,
	.item,
	.slick-list,
	.slick-track {
		height: 100%;
	}
	.form-control,
	.form-check-label:before {
		background: #f2f2f2;
		border-bottom: 0.15em solid #dbdbdb;
	}

	.title {
		color: white;

		h1,
		h2,
		p {
			background: rgba(68, 0, 153, 0.6);
			line-height: 1.3;
		}

		h1,
		h2 {
			display: initial;
		}

		p {
			display: inline-block;
		}

		${media.up('sm')} {
			h1 {
				font-size: 3rem;
			}
		}

		${media.up('md')} {
			h1 {
				font-size: 3.5rem;
			}

			margin-right: auto;
			padding-right: calc(var(--spacing) * 2);
			padding-top: calc(var(--spacing) * 2);
		}

		${media.down('md')} {
			width: 100%;
			text-align: center;
		}
	}

	.sidebar {
		padding: var(--spacing);
		background-color: white;
		flex-shrink: 0;

		${media.down('md')} {
			margin: var(--spacing) 0;
			width: 100%;
			border-radius: 5px;
		}

		${media.up('lg')} {
			width: calc(540px + 2 * var(--spacing));
			> * {
				max-width: ${widths.sm}px;
			}
		}
	}

	${media.down('xs')} {
		background-color: ${colors.brand};
	}

	${Container} {
		display: flex;
		justify-content: flex-end;

		margin-top: calc(var(--spacing) * 4);
		margin-bottom: calc(var(--spacing) * 4);

		${media.down('md')} {
			flex-wrap: wrap;
			margin-top: calc(var(--spacing) * 2);
			margin-bottom: calc(var(--spacing) * 2);
			padding: 0;
		}

		${media.down('xs')} {
			margin-top: calc(var(--spacing) * 1);
			margin-bottom: calc(var(--spacing) * 1);
			padding: 0;
		}
	}
`

export const query = graphql`
	fragment HeroFormParagraph on paragraph__hero_form {
		drupal_internal__id
		field_text {
			processed
		}
		source: field_title
		body: field_body {
			processed
		}
		relationships {
			destination: field_node {
				title
				path {
					alias
				}
			}
			field_media_multiple {
				... on media__image {
					mid: drupal_internal__mid
					relationships {
						field_media_image {
							localFile {
								childImageSharp {
									resize(width: 1600, quality: 70) {
										src
									}
								}
							}
						}
					}
				}
			}
			webform: field_webform {
				...DrupalWebform
			}
		}
	}
`
